import { QueryClient } from "@tanstack/react-query";

export class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchInterval: false,
      refetchOnWindowFocus: true,
      staleTime: 30000,
      retry: false,
    },
    mutations: {
      retry: false,
    },
  },
});

/**
 * Admin polling cadences. One place so a screen's refresh rate is a decision
 * rather than a literal someone typed.
 */
export const POLL = {
  /** Work queues — rows arrive while someone is watching the list. */
  queue: 15_000,
  /** Dashboards and badge counts — a minute stale costs nothing. */
  dashboard: 60_000,
  /** The dispute queue: deadline-driven, so it sits between the two. */
  disputes: 30_000,
} as const;

/** The `/me` profile query — the single source of "is anyone signed in?" (use-auth). */
export const AUTH_ME_QUERY_KEY = ["/api/user/auth/me"] as const;

/** Shape of the bit of the `/me` payload this module needs. */
type MeProfile = { is_soft?: boolean | null } | null;

/**
 * True for as long as one 401 reaction is settling, so a burst of parallel
 * 401s (a page that fires six queries at once) flips the session ONCE.
 */
let unauthorizedHandled = false;

/** Set when a 401 signed the member out mid-page; a surface that can explain it clears it. */
export const sessionLoss = { pending: false };

/**
 * React to a 401 from any API call: drops the cached `/me` profile so
 * `useAuth()` flips to logged-out (the query never refetches on its own).
 * No-op if already logged out, or the session is SOFT — 401s are its normal,
 * expected answer on endpoints it isn't allowed to call.
 */
export function handleUnauthorized(opts: { evenSoft?: boolean } = {}): void {
  if (unauthorizedHandled) return;
  const profile = queryClient.getQueryData<MeProfile>(AUTH_ME_QUERY_KEY);
  // `evenSoft`: the caller's route accepts soft sessions, so its 401 means the session is gone.
  if (!profile || (profile.is_soft && !opts.evenSoft)) return;
  unauthorizedHandled = true;
  sessionLoss.pending = true;
  queryClient.setQueryData<MeProfile>(AUTH_ME_QUERY_KEY, null);
  // Re-arm once this tick's responses have all been handled, so a later sign-in
  // followed by a later expiry is still noticed.
  setTimeout(() => {
    unauthorizedHandled = false;
  }, 0);
}
